import clsx from 'clsx'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import PaletteSheet from 'LuxKit/PaletteSheet'
import Group from 'components/utils/Group'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { scrollToSection } from 'weddings/lib/scrollUtils'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import type { JSXElementConstructor, ReactElement } from 'react'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Carousel from 'LuxKit/Carousel/Carousel'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import Clickable from 'components/Common/Clickable/Clickable'
import useRebrandVersion from 'hooks/useRebrandVersion'

const Palette = styled(PaletteSheet)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
`

const PageHeader = styled(Group)`
  width: 100%;
  background-color: color-mix(
    in srgb,
    ${props => props.theme.palette.neutral.surface.normalBackground} 95%,
    transparent
  );
  padding: ${rem(4)} ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};

  ${mediaQueryDown.desktop} {
    .home-button {
      padding-inline-start: 0;
    }
  }

  ${mediaQueryUp.desktop} {
    inset-inline-start: 0;
    inset-inline-end: 0;
    padding-block: ${rem(20)};
    padding-inline: ${rem(20)} ${rem(32)};
    transition: background-color 0.2s, box-shadow 0.2s;

    &.variant-dynamic {
      position: absolute;
      top: 0;
      background-color: transparent;
      border-bottom: none;
    }

    &.variant-dynamic.scrolled {
      position: fixed;
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }
`

const NavBarButton = styled(Clickable)`
  padding: ${rem(10)} ${rem(12)};
`

export interface WeddingNavItem {
  id: string
  label: string
}

interface Props {
  isPastHero?: boolean
  desktopPalette?: 'default' | 'inverse'
  variant?: 'dynamic' | 'fixed'
  navigationItems: Array<WeddingNavItem>
  primaryButtonLabel?: React.ReactNode
  primaryButtonStartIcon?: ReactElement<unknown, string | JSXElementConstructor<any>>
  primaryButtonClick?: () => void
  secondaryButtonLabel?: React.ReactNode
  secondaryButtonStartIcon?: ReactElement<unknown, string | JSXElementConstructor<any>>
  secondaryButtonClick?: () => void
}

function WeddingPageHeader(props: Props) {
  const {
    isPastHero,
    desktopPalette,
    variant = 'dynamic',
    navigationItems,
    primaryButtonLabel,
    primaryButtonClick,
    primaryButtonStartIcon,
    secondaryButtonLabel,
    secondaryButtonClick,
    secondaryButtonStartIcon,
  } = props

  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const isAboveDesktop = useScreenSizeUp('desktop')
  const isRebrand = useRebrandVersion() === 'rebrand'

  const desktopPaletteType = desktopPalette ?? (isPastHero ? 'default' : 'inverse')
  const isDesktopTranslucentHeader = isAboveDesktop && !isPastHero
  const secondaryButtonVariant = isDesktopTranslucentHeader && isRebrand ? 'dark' : 'default'

  const HeaderContent = (
    <PageHeader className={clsx({ scrolled: isPastHero }, `variant-${variant}`)} direction="vertical" gap={4}>
      <Group direction="horizontal" gap={8} horizontalAlign="space-between">
        <TextButton kind="tertiary" size="large" startIcon={<LineArrowLeftIcon />} to="/" className="home-button">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LogoIcon
              mobileVariant="icon"
              tabletVariant="icon"
              desktopVariant="icon"
              extraLargeDesktopVariant="icon"
              height={30.89}
              width={32}
              fill="currentColor"
            />
            <BodyText variant="large" weight="bold">
              {intl.formatMessage({ defaultMessage: 'Home', id: 'U6OTgpNKve_IB0qSrFUv', description: 'Navigation label for returning to the home page from a wedding page.' })}
            </BodyText>
          </Group>
        </TextButton>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {/* Desktop Navigation */}
          <CSSBreakpoint min="desktop">
            {navigationItems.map(item => (
              <TextButton key={item.id} kind="tertiary" size="large" onClick={() => scrollToSection(item.id)}>
                {item.label}
              </TextButton>
            ))}
          </CSSBreakpoint>
          {secondaryButtonLabel && secondaryButtonClick && (
            <TextButton kind="secondary" variant={secondaryButtonVariant} size={isMobile ? 'medium' : 'large'} startIcon={secondaryButtonStartIcon} shape="round" onClick={secondaryButtonClick}>
              {secondaryButtonLabel}
            </TextButton>
          )}
          {primaryButtonLabel && primaryButtonClick && (
            <TextButton kind="primary" size={isMobile ? 'medium' : 'large'} startIcon={primaryButtonStartIcon} shape="round" onClick={primaryButtonClick}>
              {primaryButtonLabel}
            </TextButton>
          )}
        </Group>
      </Group>
      {/* Mobile Navigation */}
      <CSSBreakpoint max="tablet">
        <Carousel>
          {navigationItems.map(item => (
            <NavBarButton key={item.id} onClick={() => scrollToSection(item.id)}>
              <BodyText variant="medium" wrap="no-wrap">{item.label}</BodyText>
            </NavBarButton>
          ))}
        </Carousel>
      </CSSBreakpoint>
    </PageHeader>
  )

  return (<>
    {/* Mobile Header */}
    {!isAboveDesktop && HeaderContent}
    {/* Desktop Header */}
    {isAboveDesktop && (
      <Palette paletteType={desktopPaletteType}>
        {HeaderContent}
      </Palette>
    )}
  </>)
}

export default WeddingPageHeader
